/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMemo } from 'react';

import { usePaginatedUserSubtenantsQuery } from '@/hooks/query/usePaginatedUserSubtenantsQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseUserSubtenantRoleQueryParams = {
  subtenantId?: string;
  parentId?: string;
  enabled?: boolean;
};

/**
 * Custom hook that fetches the current user's role for a specific subtenant.
 * Uses the existing paginated user subtenants endpoint by querying with the parent ID
 * and finding the role for the target subtenant in the returned children.
 *
 * @param params Configuration object:
 *   - subtenantId: The subtenant ID to get the role for
 *   - parentId: The parent subtenant ID to query (required for the endpoint)
 *   - enabled: Whether to enable the query
 *
 * @returns Object containing:
 *   - role: The user's role for the specified subtenant
 *   - isLoading: Boolean for loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 */
export function useUserSubtenantRoleQuery({ subtenantId, parentId, enabled = true }: UseUserSubtenantRoleQueryParams) {
  const currentUser = useMgmtPlaneStateStore((state) => state.userProfile);

  // Only query if we have the required data and enabled is true
  const shouldQuery = enabled && !!currentUser?.id && !!subtenantId;

  // Query the user subtenants with the parent ID
  const { data, isLoading, isError, error } = usePaginatedUserSubtenantsQuery({
    params: {
      id: currentUser?.id || '',
      filters: parentId ? { subtenantId: parentId } : undefined,
    },
    type: 'inMemory',
    enabled: shouldQuery,
  });

  // Find the role for the specific subtenant in the returned data
  const role: 'none' | 'viewer' | 'editor' | 'admin' | undefined = useMemo(() => {
    if (!data?.data || !subtenantId) return undefined;

    // Look for the subtenant in the returned data
    const subtenantData = data.data.find((item) => item.subtenant.id === subtenantId);
    return subtenantData?.role;
  }, [data, subtenantId]);

  return {
    role,
    isLoading,
    isError,
    error,
  };
}
